import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Live investor-report feed.
 *
 * Loads the current snapshot once, then holds a Server-Sent Events stream open
 * so the page updates as soon as the poller sees a change — typically ~2s after
 * a position opens, closes or moves.
 *
 * Why fetch + ReadableStream instead of EventSource: EventSource cannot send an
 * Authorization header, and this route is admin-only. Passing the JWT in the
 * query string would leak it into server access logs, so we read the SSE body
 * manually instead.
 *
 * The server sends two event types:
 *   snapshot — the full report, only when something MATERIALLY changed
 *   tick     — a few bytes saying "checked, nothing changed", so the UI can show
 *              freshness without re-receiving ~14 KB every couple of seconds
 *
 * `path` selects which view of the same snapshot to read: the default real
 * figures, or "/api/investor-report/normalised" for the model-portfolio page.
 * Both speak the identical payload shape and both stream, so one hook serves
 * both pages and they cannot drift apart.
 */

const API = "/api/investor-report";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * @param {string} path        which view of the snapshot to read
 * @param {{container?: string}} [opts]  which CONTAINER to ask for. Empty means
 *        "let the server pick", and it picks the DEFAULT container — never All.
 *        Nothing here decides that; the response says which it chose.
 */
export default function useInvestorReport(path = API, { container = "" } = {}) {
  const [report, setReport] = useState(null);
  const [poller, setPoller] = useState(null);
  const [error, setError] = useState(null);
  const [live, setLive] = useState(false);
  /**
   * POLLING BY DESIGN, as opposed to a stream that dropped.
   *
   * These two look identical from `live === false` and mean opposite things: one
   * is a page refetching on a timer exactly as intended, the other is a broken
   * connection. Reporting the first as "reconnecting…" tells the reader
   * something is wrong with a page where nothing is. Only this hook knows
   * which, so only it can say.
   */
  const [polling, setPolling] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);

  /**
   * WHICH SELECTION THE DATA ON SCREEN BELONGS TO.
   *
   * Changing container tears down a stream and starts a new request, and the old
   * stream can deliver one more snapshot on its way out. That snapshot is a
   * DIFFERENT CONTAINER'S, and applying it repaints the page with figures
   * belonging to the selection the reader just left — under the name of the one
   * they just chose. Every number plausible, all of them the wrong book's.
   *
   * Observed, not theorised: selecting All showed the previous container's
   * figures with no banner, because the outgoing stream's snapshot arrived after
   * the new request had already failed and cleared the error on its way in.
   *
   * So every request and every stream is stamped with the generation it was
   * started in, and anything from an older one is dropped.
   */
  const genRef = useRef(0);
  const pollRef = useRef(null);
  const abortRef = useRef(null);
  const retryRef = useRef(null);
  const backoffRef = useRef(1000);
  const mountedRef = useRef(true);

  const applySnapshot = useCallback((payload, gen) => {
    /* Late arrival from a superseded selection — drop it silently. Silent
       because it is the NORMAL consequence of switching container, not a
       fault. */
    if (gen !== undefined && gen !== genRef.current) return;

    /* THE STREAM CARRIES THE REPORT, NOT THE SELECTOR.
       /stream sends the report payload and nothing else, so a snapshot has no
       `containers` and no `selection`. Assigning it straight over the fetched
       body would erase both the moment the stream connected, and the container
       dropdown would disappear from the header a second after the page loaded
       with every figure on it still correct.

       So those two are carried forward: they describe which container is being
       shown and what else may be chosen, which a snapshot does not speak to. */
    setReport((prev) => {
      if (!prev) return payload;
      const merged = { ...payload };
      if (merged.containers === undefined && prev.containers) merged.containers = prev.containers;
      if (merged.selection === undefined && prev.selection) merged.selection = prev.selection;
      return merged;
    });
    if (payload.poller) setPoller(payload.poller);
    setCheckedAt(Date.now());
    setError(null);
  }, []);

  /**
   * The URL for this selection. `?container=` is omitted entirely when nothing
   * is chosen, so the server applies its own default rather than being told one
   * by a page that cannot know which containers exist yet.
   */
  const url = container ? `${path}?container=${encodeURIComponent(container)}` : path;

  /**
   * THE STREAM'S URL, WHICH ALSO HAS TO CARRY THE CONTAINER.
   *
   * This was `${path}/stream` with no query at all, so the stream always
   * resolved to the DEFAULT container however the page was scoped. The one-shot
   * fetch loaded the chosen container correctly and the stream then overwrote it
   * with the default's snapshot on its first message and every message after —
   * so selecting any non-default container showed the default's accounts and
   * figures under the chosen container's name.
   *
   * The generation guard could not catch it: the stream was current, not stale.
   * It was simply pointed somewhere else. And `/stream` sends no `selection`, so
   * nothing in the payload contradicted the heading.
   */
  const streamUrl = container
    ? `${path}/stream?container=${encodeURIComponent(container)}`
    : `${path}/stream`;

  /** One-shot load, so the page paints even if streaming is unavailable. */
  const loadOnce = useCallback(async () => {
    /* Captured at the START of the request. If the container changes while this
       is in flight, the response belongs to the selection the reader left. */
    const gen = genRef.current;
    try {
      const res = await fetch(url, { headers: authHeaders() });
      // The API is public, so this should not happen. If it does, auth has been
      // re-enabled server-side without re-wrapping the route in App.js.
      if (res.status === 401 || res.status === 403) {
        setError("This report's API is protected but the page is not signed in.");
        return false;
      }
      const body = await res.json();
      if (gen !== genRef.current) return null;   // superseded selection
      if (!res.ok) {
        setPoller(body.poller || null);
        setError(body.message || `Request failed (${res.status})`);
        /* THE STALE REPORT GOES. Without this the page keeps rendering the
           previous container's figures under the newly chosen container's name,
           which is worse than showing nothing: the reader has no way to tell.
           `containers` is preserved so the selector still works and they can
           choose something that does resolve. */
        setReport((prev) => (prev ? { containers: body.containers || prev.containers, selection: body.selection } : prev));
        /* The BODY is returned even on failure, because a 503 still carries the
           container list and the page needs it to draw its selector — without
           it the reader cannot switch to a container that would work. */
        return body;
      }
      applySnapshot(body, gen);
      return body;
    } catch (err) {
      setError(`Could not reach the API — ${err.message}`);
      return null;
    }
  }, [applySnapshot, url]);

  /** Hold the SSE stream open, reconnecting with backoff if it drops. */
  const connectStream = useCallback(() => {
    /* The generation this stream belongs to. A snapshot arriving from a stream
       started before the container changed is dropped by applySnapshot. */
    const gen = genRef.current;
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const res = await fetch(streamUrl, {
          headers: { ...authHeaders(), Accept: "text/event-stream" },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`stream failed (${res.status})`);

        setLive(true);
        backoffRef.current = 1000; // a successful connect resets the backoff

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line.
          let split;
          while ((split = buffer.indexOf("\n\n")) !== -1) {
            const frame = buffer.slice(0, split);
            buffer = buffer.slice(split + 2);

            let event = "message";
            const dataLines = [];
            for (const line of frame.split("\n")) {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
              // lines starting with ":" are keep-alive comments — ignore
            }
            if (!dataLines.length) continue;

            let data;
            try {
              data = JSON.parse(dataLines.join("\n"));
            } catch {
              continue;
            }

            if (event === "snapshot") applySnapshot(data, gen);
            else if (event === "tick") {
              if (data.poller) setPoller(data.poller);
              setCheckedAt(Date.now());
            } else if (event === "fatal") setError(data.message);
            else if (event === "waiting") {
              if (data.poller) setPoller(data.poller);
              setError(data.message || "Poller starting…");
            }
          }
        }
        throw new Error("stream ended");
      } catch (err) {
        if (controller.signal.aborted || !mountedRef.current) return;
        setLive(false);
        const wait = backoffRef.current;
        backoffRef.current = Math.min(wait * 2, 20000);
        retryRef.current = setTimeout(connectStream, wait);
      }
    })();
    /* `streamUrl`, NOT `path`. Without it this callback kept the URL it was
       created with, so changing container tore the stream down and reconnected
       it to the same container it had before. */
  }, [applySnapshot, streamUrl]);

  /**
   * Refetch on a timer, for a container the stream does not speak for.
   *
   * Fifteen seconds: often enough that the figures are current to the reader,
   * rare enough that a container nobody is polling costs almost nothing.
   */
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (mountedRef.current) loadOnce();
    }, 15000);
  }, [loadOnce]);

  useEffect(() => {
    mountedRef.current = true;
    /* A NEW SELECTION IS A NEW GENERATION. Bumped before anything is started,
       so every request and stream from the previous one is already stale. */
    genRef.current += 1;

    loadOnce().then((body) => {
      if (!mountedRef.current) return;
      /**
       * THE SERVER DECIDES whether this may be streamed. `streamable` is set
       * only on a response the poller's own stream actually speaks for.
       *
       * Deciding it here instead — "one container, so stream it" — is wrong the
       * moment a second container exists: /stream carries whichever container
       * the poller is running, so it would push that one's figures into a page
       * showing another, labelled live.
       */
      const streamable = Boolean(body && body.streamable);
      setPolling(!streamable);
      if (streamable) connectStream();
      else {
        setLive(false);
        startPolling();
      }
    });

    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
      if (retryRef.current) clearTimeout(retryRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadOnce, connectStream, startPolling]);

  /* Refresh always targets the base route: there is a single poller behind both
     views, so refreshing the normalised page refetches the same history. */
  /** Ask the poller to refetch history immediately. */
  const refresh = useCallback(async () => {
    try {
      await fetch(`${API}/refresh`, { method: "POST", headers: authHeaders() });
    } catch {
      /* the stream will deliver the result anyway */
    }
  }, []);

  return { report, poller, error, live, polling, checkedAt, refresh, reload: loadOnce };
}
